import { Stack, useTheme } from '@mui/material';
import {
  createContext,
  Fragment,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useDragLayer } from 'react-dnd';
import { useLocation } from 'react-router-dom';
import { usePage } from '../../usePage';
import { useWidth } from '../../useWidth';

type Column = { id: string; width: number };
type RegisterColumn = (columnId: string, initialWidth?: number) => void;
type ResizeColumn = (columnId: string, deltaWidth: number) => void;

const ColumnSizeContext = createContext<
  | {
      pageColumns: Record<string, Column[]>;
      maxWidth?: number;
      registerColumn: RegisterColumn;
      resizeColumn: ResizeColumn;
    }
  | undefined
>(undefined);

export const ColumnResizerProvider = ({ children }: PropsWithChildren) => {
  const [pageColumns, setPageColumns] = useState<Record<string, Column[]>>({});
  const [prevOffset, setPrevOffset] = useState(0);

  const parentRef = useRef<HTMLDivElement>(null);
  const maxWidth = useWidth(parentRef);

  const page = usePage();
  const theme = useTheme();
  const spacingPercent = maxWidth ? Number(theme.spacing(1).slice(0, -2)) / maxWidth : 0;

  const dragEvent = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    offset: monitor.getDifferenceFromInitialOffset()?.x
  }));

  useEffect(() => {
    const { item, offset } = dragEvent;
    if (item && offset) {
      const delta = offset - prevOffset;
      resizeColumn(item.id, delta * (item.left ? -1 : 1));
      setPrevOffset(offset);
    } else {
      setPrevOffset(0);
    }
  }, [dragEvent]);

  const setColumns = useCallback(
    (setColumns: (currentColumns: Column[]) => Column[]) => {
      setPageColumns((currPageColumns) => ({
        ...currPageColumns,
        [page]: setColumns(currPageColumns?.[page] ?? [])
      }));
    },
    [page, setPageColumns]
  );

  const resizeColumn = useCallback(
    (columnId: string, deltaWidth: number) => {
      const deltaWidthPercent = maxWidth ? deltaWidth / maxWidth : 0;
      setColumns((currColumns) => {
        const sumWidthPercent = currColumns.reduce(
          (acc, currColumn) => acc + currColumn.width + spacingPercent * 2,
          0
        );
        if (sumWidthPercent + deltaWidthPercent > 1) {
          return currColumns;
        } else {
          return currColumns.map((currColumn) =>
            currColumn.id === columnId
              ? { ...currColumn, width: currColumn.width + deltaWidthPercent }
              : currColumn
          );
        }
      });
    },
    [maxWidth, page]
  );

  const registerColumn = (columnId: string, initialWidth?: number) => {
    setColumns((currColumns) =>
      currColumns.some((column) => column.id === columnId)
        ? currColumns
        : [...currColumns, { id: columnId, width: initialWidth ?? 0.3 }]
    );
  };

  return (
    <ColumnSizeContext.Provider value={{ pageColumns, maxWidth, registerColumn, resizeColumn }}>
      <Stack
        ref={parentRef}
        direction={'row'}
        flexGrow={1}
        justifyContent="space-between">
        {children}
      </Stack>
    </ColumnSizeContext.Provider>
  );
};

export const useColumnResizer = (columnId?: string, initialWidth?: number) => {
  const context = useContext(ColumnSizeContext);
  const page = usePage();

  if (context === undefined) {
    throw new Error('useColumnResizer must be used within  a ColumnResizerProvider');
  }

  const { pageColumns, maxWidth, registerColumn } = context;

  useEffect(() => {
    if (
      columnId &&
      (pageColumns[page] === undefined ||
        !pageColumns[page].some((column) => column.id === columnId))
    ) {
      registerColumn(columnId, initialWidth);
    }
  }, [pageColumns, page, columnId, registerColumn]);

  return {
    columnWidth:
      (maxWidth ?? 1000) *
      (pageColumns[page]?.find((column) => column.id === columnId)?.width ?? 0.3)
  };
};
